'use client'

import { useState } from 'react'

import { useMutation } from '@tanstack/react-query'
import { erc20Abi } from 'viem'
import {
  useAccount,
  useConfig,
  useSendTransaction,
  useSwitchChain,
  useWriteContract,
} from 'wagmi'
import { waitForTransactionReceipt } from 'wagmi/actions'

import { formatWalletErrorMessage } from '~/components/forms/deposit/wallet/utils'
import { EvmAddress } from '~/utils/address'
import { isNativeTokenAddress } from '~/utils/tokens'

type TransferPhase = 'switching' | 'signing' | 'confirming'

export const useWalletDepositTransfer = ({
  treasuryAddress,
}: {
  treasuryAddress: EvmAddress.Type
}) => {
  const [transferPhase, setTransferPhase] = useState<TransferPhase>('signing')
  const { chainId: connectedChainId } = useAccount()
  const { switchChainAsync } = useSwitchChain()
  const { writeContractAsync } = useWriteContract()
  const { sendTransactionAsync } = useSendTransaction()
  const config = useConfig()

  const mutation = useMutation({
    mutationFn: async ({
      tokenAddress,
      chainId,
      amount,
    }: {
      tokenAddress: EvmAddress.Type
      chainId: number
      amount: bigint
    }) => {
      if (connectedChainId !== chainId) {
        setTransferPhase('switching')
        await switchChainAsync({ chainId })
      }

      setTransferPhase('signing')
      const to = EvmAddress.format(treasuryAddress)
      const hash = isNativeTokenAddress(tokenAddress)
        ? await sendTransactionAsync({ to, value: amount, chainId })
        : await writeContractAsync({
            address: EvmAddress.format(tokenAddress),
            abi: erc20Abi,
            functionName: 'transfer',
            args: [to, amount],
            chainId,
          })

      setTransferPhase('confirming')
      const receipt = await waitForTransactionReceipt(config, {
        hash,
        chainId,
      })
      if (receipt.status === 'reverted') throw new Error('Transaction reverted')
      return receipt
    },
  })

  return {
    sendTransfer: mutation.mutate,
    isTransferring: mutation.isPending,
    transferPhase,
    isSuccess: mutation.isSuccess,
    errorMessage: mutation.isError
      ? formatWalletErrorMessage(mutation.error)
      : null,
    resetTransfer: mutation.reset,
  }
}
